import React, { useEffect, useState } from "react";
import { API_ORIGIN, authFetch } from "../config/apiConfig";
import { canDelete } from "../utils/permissions";

const API_BASE = API_ORIGIN;

// Same icon set as UsersList.jsx / DepartmentManagement.jsx's Action
// column (no shared icons module in this project yet -- each management
// page defines its own small copy, matching the existing convention).
const EyeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 20h9" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="10" y1="11" x2="10" y2="17" strokeLinecap="round" />
    <line x1="14" y1="11" x2="14" y2="17" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// TEMP: this page was showing a blank white screen for the user in
// production -- something in CompanyManagementImpl below throws at
// runtime, but the exact error hasn't been diagnosed yet (would need
// the browser console output). Per request, swapping the real page out
// for this placeholder for now rather than shipping a blank screen.
// CompanyManagementImpl and everything it depends on is untouched
// below -- once the console error is known and fixed, restore this by
// renaming CompanyManagementImpl back to CompanyManagement and
// deleting this wrapper (and the "export default" above
// CompanyManagementImpl).
export default function CompanyManagement() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body text-center py-5">
        <i className="ti ti-rocket fs-1 text-muted d-block mb-3" />
        <h5 className="text-muted mb-0">This Page is Part of next release</h5>
      </div>
    </div>
  );
}

function CompanyManagementImpl() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null); // { type: "success"|"error", text }

  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [viewing, setViewing] = useState(null); // company object being viewed, or null

  const loadCompanies = () => {
    setLoading(true);
    authFetch(`${API_BASE}/companies`)
      .then((res) => res.json())
      .then((data) => setCompanies(Array.isArray(data) ? data : []))
      .catch(() => setMessage({ type: "error", text: "Could not load companies." }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const handleAddCompany = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!companyName.trim()) {
      setMessage({ type: "error", text: "Please enter a company name." });
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/companies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ CompanyName: companyName.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to create company." });
        return;
      }

      setMessage({ type: "success", text: "Data successfully added." });
      setCompanyName("");
      loadCompanies();
    } catch (err) {
      setMessage({ type: "error", text: "Network error while creating the company." });
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (c) => {
    setMessage(null);
    setEditingId(c.CompanyID);
    setEditingName(c.CompanyName);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = async (id) => {
    if (!editingName.trim()) {
      setMessage({ type: "error", text: "Please enter a company name." });
      return;
    }
    setSavingEdit(true);
    try {
      const res = await authFetch(`${API_BASE}/companies/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ CompanyName: editingName.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to update company." });
        return;
      }

      setMessage({ type: "success", text: "Data successfully updated." });
      setEditingId(null);
      setEditingName("");
      loadCompanies();
    } catch (err) {
      setMessage({ type: "error", text: "Network error while updating the company." });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete "${c.CompanyName}"? This can't be undone.`)) return;

    setMessage(null);
    setDeletingId(c.CompanyID);
    try {
      const res = await authFetch(`${API_BASE}/companies/${c.CompanyID}`, { method: "DELETE" });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to delete company." });
        return;
      }

      setMessage({ type: "success", text: "Company deleted." });
      loadCompanies();
    } catch (err) {
      setMessage({ type: "error", text: "Network error while deleting the company." });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container-fluid mt-4" style={{ maxWidth: 900 }}>
      <h4 className="master-page-title">Companies</h4>

      <div className="master-toolbar">
        <label htmlFor="companyName" className="master-toolbar-label">
          Company
        </label>
        <form className="master-toolbar-row" onSubmit={handleAddCompany}>
          <input
            id="companyName"
            type="text"
            className="master-input"
            placeholder="e.g. Acme Contracting Co."
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
          <button type="submit" className="master-add-btn" disabled={submitting}>
            {submitting ? "ADDING..." : "ADD"}
          </button>
        </form>

        {message && (
          <p className={`master-msg ${message.type === "success" ? "master-msg-success" : "master-msg-error"}`}>
            {message.text}
          </p>
        )}
      </div>

      <div className="master-table-wrap">
        <table className="master-table">
          <thead>
            <tr>
              <th>Company ID</th>
              <th>Company Name</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="text-center">Loading...</td>
              </tr>
            ) : companies.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center">No companies yet.</td>
              </tr>
            ) : (
              companies.map((c) => (
                <tr key={c.CompanyID}>
                  <td>{c.CompanyID}</td>
                  <td>
                    {editingId === c.CompanyID ? (
                      <input
                        type="text"
                        className="master-edit-input"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      c.CompanyName
                    )}
                  </td>
                  <td>
                    <div className="project-actions">
                      {editingId === c.CompanyID ? (
                        <>
                          <button
                            className="icon-btn"
                            title="Save"
                            onClick={() => saveEdit(c.CompanyID)}
                            disabled={savingEdit}
                          >
                            <CheckIcon />
                          </button>
                          <button className="icon-btn" title="Cancel" onClick={cancelEdit} disabled={savingEdit}>
                            <XIcon />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="icon-btn" title="View" onClick={() => setViewing(c)}>
                            <EyeIcon />
                          </button>
                          <button className="icon-btn" title="Edit" onClick={() => startEdit(c)}>
                            <EditIcon />
                          </button>
                          {/* Super Admin only per the Role Matrix -- Admin's
                              Master cell is Add-Edit, no Delete. */}
                          {canDelete("Master") && (
                            <button
                              className="icon-btn"
                              title="Delete"
                              onClick={() => handleDelete(c)}
                              disabled={deletingId === c.CompanyID}
                            >
                              <TrashIcon />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {viewing && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000
          }}
          onClick={() => setViewing(null)}
        >
          <div
            style={{ background: "#fff", borderRadius: 8, padding: "24px 28px", minWidth: 320, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 style={{ margin: "0 0 16px", color: "#154749", fontWeight: 700 }}>Company Details</h5>
            <p style={{ margin: "0 0 8px", fontSize: 14 }}><strong>Company ID:</strong> {viewing.CompanyID}</p>
            <p style={{ margin: "0 0 20px", fontSize: 14 }}><strong>Company Name:</strong> {viewing.CompanyName}</p>
            <div style={{ textAlign: "right" }}>
              <button type="button" className="master-add-btn" style={{ padding: "8px 20px" }} onClick={() => setViewing(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}